import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Theme,
  Typography,
} from "@material-ui/core";
import { DeleteOutlined } from "@material-ui/icons";
import React, { useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PulseLoader } from "react-spinners";
import { UserFavouritesResponse } from "../../../../api/smartmelts-api/graphql/generated";
import { ContentText } from "../../../../components/text/content-text";
import { HeadingText } from "../../../../components/text/heading-text";
import { MenuCategoryDictionary, MenuSubcategoryDictionary } from "../../../../models/categories";
import { theme } from "../../../../theme/theme";
import { AnswerContext } from "../../answer/context/answer-context";
import { NutritionContext } from "../../context/nutrition-context";

interface IUserFavouritesProps {}

const ICON_SIZE = 30;

const useStyles = makeStyles((theme: Theme) => ({
  listItem: {
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    cursor: "pointer",
  },
  listItemText: {
    fontWeight: "bold",
  },
  subheading: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
  },
  listDeleteAction: { borderLeft: `1px solid ${theme.palette.primary.main}`, paddingLeft: theme.spacing(2) },
  loader: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
}));

export const UserFavourites: React.VFC<IUserFavouritesProps> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    changeActiveCategories,
    onUpdateUserFavourite,
    favouritesLoading,
    userFavourites,
    onLoadFavourites,
  } = useContext(NutritionContext);

  const { setActiveAnswerIndex } = useContext(AnswerContext);

  const handleFavourites = useCallback(
    (favourite) => {
      const currentSubcategory = MenuSubcategoryDictionary[favourite.subcategory];
      if (currentSubcategory.isQuestionBased) {
        setActiveAnswerIndex(favourite.index - 1);
      }
      changeActiveCategories(favourite.category, favourite.subcategory, favourite.index);
    },
    [changeActiveCategories, setActiveAnswerIndex],
  );

  const favouriteInfo = (favourite: UserFavouritesResponse) => {
    return `${favourite.subcategory}.${favourite.index ?? 1}`;
  };

  useEffect(() => {
    onLoadFavourites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDeleteFavourite = (favourite: UserFavouritesResponse) => {
    onUpdateUserFavourite(favourite.category, favourite.subcategory, favourite.index);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <HeadingText>{t("favourites.header")}</HeadingText>
      </Grid>
      <Grid item xs={12} className={classes.subheading}>
        <ContentText light>{t("favourites.subheader")}</ContentText>
      </Grid>
      <Grid item xs={12}>
        {favouritesLoading && (
          <Box className={classes.loader}>
            <PulseLoader size={32} color={theme.palette.primary.main} />
          </Box>
        )}
        {!favouritesLoading && userFavourites && (
          <List>
            {userFavourites.map((favourite, index) => {
              const { Icon, color } = MenuCategoryDictionary[favourite.category];

              return (
                <ListItem key={index} className={classes.listItem} onClick={() => handleFavourites(favourite)}>
                  <ListItemIcon>{Icon && <Icon height={ICON_SIZE} width={ICON_SIZE} color={color} />}</ListItemIcon>
                  <ListItemText>
                    <Typography className={classes.listItemText}>
                      {t(`menu_item_category.${favourite.category}`)} {" > "}
                      {t(`menu_item_subcategories.${favourite.subcategory}`)}
                    </Typography>
                    <Typography>{t(`favourites.info.${favouriteInfo(favourite)}`)}</Typography>
                  </ListItemText>
                  <ListItemSecondaryAction className={classes.listDeleteAction}>
                    <DeleteOutlined
                      style={{ color: theme.palette.primary.main, cursor: "pointer" }}
                      onClick={() => onDeleteFavourite(favourite)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </Grid>
    </Grid>
  );
};
